import { useEffect, useState } from 'react';
import { installConnectionMonitor, getConnectionStatus, subscribeConnection } from '@/utils/connectionStatus';
import { installRequestTimeout } from '@/utils/requestTimeout';
import { installReadCache, getStaleAt, subscribeStale } from '@/utils/readCache';
import { installLocationAssignReminder } from '@/utils/locationAssignReminder';
import { toast } from 'sonner';
import { startOutboxRunner, resume as resumeOutbox, getPendingCount, subscribe as subscribeOutbox, isScreenActive } from '@/utils/chatOutbox';
import { failedOutboxMessage } from '@/components/chat/useChatOutbox';

// Dipasang saat modul dimuat (bukan di effect): efek layar anak berjalan lebih dulu daripada
// efek komponen ini, jadi pemanggilan data pertama akan terlewat kalau dipasang di effect.
installRequestTimeout();
// Harus dipasang SETELAH installRequestTimeout: permintaan melewati pencegat terakhir-dipasang lebih dulu,
// sehingga batas waktu pendek dari cache menang atas batas bawaan 30 detik.
installReadCache();
// Pengingat izin lokasi nakes saat admin menugaskan nakes (hanya membaca, tidak menghalangi penugasan).
installLocationAssignReminder();

/**
 * Tingkat aplikasi (Okt 2026, Fase 4; banner dipangkas Okt 2026):
 *  - menjalankan pengirim ulang pesan chat yang tertunda
 *  - menampilkan info kecil hanya kalau offline atau ada pesan chat menunggu dikirim
 * Tidak memblokir klik apa pun (pointer-events: none).
 */
export default function ConnectivityLayer() {
  const [status, setStatus] = useState(getConnectionStatus());
  const [pending, setPending] = useState(getPendingCount());
  const [staleAt, setStaleAt] = useState(getStaleAt());

  useEffect(() => {
    const stopMonitor = installConnectionMonitor();
    const stopRunner = startOutboxRunner();
    setStatus(getConnectionStatus());
    const offConn = subscribeConnection(setStatus);
    const offStale = subscribeStale((t) => setStaleAt(t));
    const onBackOnline = () => {
      if (getStaleAt()) {
        toast.info('Sinyal kembali. Muat ulang untuk melihat data terbaru.', {
          id: 'read-cache-back',
          duration: 2000,
          action: { label: 'Muat ulang', onClick: () => window.location.reload() },
        });
      }
    };
    window.addEventListener('online', onBackOnline);
    const refresh = () => setPending(getPendingCount());
    const tickResume = () => { refresh(); resumeOutbox(); };
    const offOutbox = subscribeOutbox((evt) => {
      refresh();
      // Pesan antrean gagal permanen tapi layar chat-nya sudah ditutup: beri tahu di sini.
      if (evt && evt.type === 'failed' && evt.item && !isScreenActive(evt.item.chatKey)) {
        toast.error(failedOutboxMessage(evt));
      }
    });
    // Jumlah juga berubah saat login/logout ganti user; cek berkala ringan.
    const poll = setInterval(tickResume, 5000);
    refresh();
    return () => {
      offConn();
      offStale();
      window.removeEventListener('online', onBackOnline);
      offOutbox();
      clearInterval(poll);
      stopRunner();
      stopMonitor();
    };
  }, []);

  // Okt 2026: banner dipangkas. Hanya dua informasi yang memengaruhi tindakan pengguna:
  // benar-benar offline, dan jumlah pesan chat yang menunggu dikirim. Pesan umum
  // "sinyal lemah" dihapus karena sering salah (respons lambat dari server bukan sinyal lemah).
  let text = null;
  if (status === 'offline') {
    text = pending > 0
      ? `Tidak ada koneksi. ${pending} pesan akan dikirim saat sinyal kembali.`
      : 'Tidak ada koneksi.';
  } else if (pending > 0) {
    text = `${pending} pesan menunggu dikirim.`;
  }
  if (staleAt) {
    const d = new Date(staleAt);
    const jam = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    const hariIni = new Date().toDateString() === d.toDateString();
    const waktu = hariIni ? `jam ${jam}` : `${d.getDate()}/${d.getMonth() + 1} jam ${jam}`;
    const stale = `Data terakhir ${waktu}, belum diperbarui. Muat ulang saat sinyal kembali.`;
    text = text ? `${text} ${stale}` : stale;
  }
  if (!text) return null;

  return (
    <div
      data-testid="connectivity-banner"
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        top: 'calc(env(safe-area-inset-top, 0px) + 52px)',
        display: 'flex',
        justifyContent: 'center',
        zIndex: 70,
        pointerEvents: 'none',
        padding: '0 12px',
      }}
    >
      <div
        style={{
          background: status === 'offline' ? '#475569' : '#b45309',
          color: '#fff',
          fontSize: 12,
          lineHeight: '16px',
          padding: '5px 12px',
          borderRadius: 999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
          maxWidth: 380,
          textAlign: 'center',
        }}
      >
        {text}
      </div>
    </div>
  );
}
